import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { SectionCard, EmptyState } from "../../components/Common";
import { UserPlus, Users, Stethoscope, X, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { key: "doctor",           label: "Doctors",             subLabel: "Specialty" },
  { key: "nurse",            label: "Nurses",              subLabel: "Department" },
  { key: "lab_tech",         label: "Lab technicians",     subLabel: "Department" },
  { key: "pharmacist",       label: "Pharmacists",         subLabel: "Department" },
  { key: "radiologist",      label: "Radiology",           subLabel: "Department" },
  { key: "receptionist",     label: "Reception",           subLabel: "Department" },
  { key: "cashier",          label: "Billers / Cashiers",  subLabel: "Department" },
  { key: "records_officer",  label: "Records Officers",    subLabel: "Department" },
  { key: "cfo",              label: "Finance",             subLabel: "Department" },
];

export function StaffManagerCard() {
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/staff");
      setStaff(data || []);
    } catch { toast.error("Failed to load staff"); }
  };
  useEffect(() => { load(); }, []);

  const byRole = useMemo(() => {
    const out = {};
    for (const r of ROLES) out[r.key] = [];
    for (const u of staff) if (out[u.role]) out[u.role].push(u);
    return out;
  }, [staff]);

  const removeStaff = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try { await api.delete(`/staff/${u.user_id}`); toast.success("Staff removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const rolesToRender = tab === "all" ? ROLES : ROLES.filter((r) => r.key === tab);

  return (
    <SectionCard
      title="Staff"
      testId="staff-card"
      actions={
        <button data-testid="add-staff-btn" onClick={() => setShowAdd(true)} className="h-9 px-3 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-xs font-semibold flex items-center gap-1.5">
          <UserPlus className="w-4 h-4" /> Add staff
        </button>
      }
    >
      <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-1 overflow-x-auto ths-nav-scroll" data-testid="staff-tabs">
        <TabPill active={tab === "all"} onClick={() => setTab("all")} testId="stab-all">All ({staff.length})</TabPill>
        {ROLES.map((r) => (
          <TabPill key={r.key} active={tab === r.key} onClick={() => setTab(r.key)} testId={`stab-${r.key}`}>
            {r.label} ({byRole[r.key]?.length || 0})
          </TabPill>
        ))}
      </div>

      <div className="p-4 space-y-4">
        {rolesToRender.map((r) => {
          const list = byRole[r.key] || [];
          if (list.length === 0 && tab !== "all") {
            return <EmptyState key={r.key} icon={Users} title={`No ${r.label.toLowerCase()} yet`} description={`Click "Add staff" and pick ${r.label.slice(0,-1)}.`} />;
          }
          if (list.length === 0) return null;
          const groups = list.reduce((acc, u) => {
            const g = u.specialty || u.department || "General";
            (acc[g] = acc[g] || []).push(u); return acc;
          }, {});
          return (
            <div key={r.key} className="space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> {r.label} · {list.length}
              </div>
              {Object.entries(groups).map(([grp, users]) => (
                <div key={grp} className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {grp} · {users.length}</div>
                  {users.map((u) => (
                    <div key={u.user_id} data-testid={`staff-row-${u.user_id}`} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                        <div className="text-xs text-slate-500 tabular truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button data-testid={`edit-${u.user_id}`} onClick={() => setEditing(u)} className="h-8 px-3 rounded-md border border-slate-200 text-xs font-medium hover:bg-slate-50 flex items-center gap-1"><Edit2 className="w-3 h-3" /> Edit</button>
                        <button data-testid={`del-${u.user_id}`} onClick={() => removeStaff(u)} className="h-8 px-3 rounded-md border border-red-200 text-[#B71C1C] text-xs font-medium hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
        {tab === "all" && staff.length === 0 && (
          <EmptyState icon={Users} title="No staff registered yet" description='Click "Add staff" to register your first team member.' />
        )}
      </div>

      {showAdd && <StaffModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
      {editing && <StaffModal existing={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </SectionCard>
  );
}

function TabPill({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`h-8 px-3 rounded-md text-xs font-semibold flex-shrink-0 ${active ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}
    >{children}</button>
  );
}

function StaffModal({ existing, onClose, onDone }) {
  const editMode = !!existing;
  const [step, setStep] = useState(editMode ? 2 : 1);
  const [role, setRole] = useState(existing?.role || "");
  const meta = ROLES.find((r) => r.key === role);
  const [form, setForm] = useState({
    name: existing?.name || "",
    email: existing?.email || "",
    password: "",
    specialty: existing?.specialty || "",
    department: existing?.department || "",
    phone: existing?.phone || "",
  });
  const [busy, setBusy] = useState(false);
  const isDoctor = role === "doctor";
  const subField = isDoctor ? "specialty" : "department";

  const valid = editMode
    ? form.name.trim() && form.email.trim()
    : role && form.name.trim() && form.email.trim() && form.password;

  const submit = async () => {
    if (!valid) { toast.error("Fill in the required fields"); return; }
    setBusy(true);
    try {
      if (editMode) {
        const patch = { name: form.name, email: form.email, phone: form.phone };
        patch[subField] = form[subField];
        if (form.password) patch.password = form.password;
        await api.patch(`/staff/${existing.user_id}`, patch);
        toast.success("Staff updated");
      } else {
        const body = { role, name: form.name, email: form.email, password: form.password, phone: form.phone };
        body[subField] = form[subField];
        await api.post("/staff", body);
        toast.success("Staff added");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="staff-modal" className="bg-white rounded-2xl border border-slate-200 max-w-md w-full shadow-xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">{editMode ? `Edit staff · ${existing.name}` : (step === 1 ? "Add staff · pick a role" : `Add staff · ${meta?.label?.slice(0,-1) || role}`)}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        {step === 1 ? (
          <div className="p-4 space-y-1.5" data-testid="role-picker">
            {ROLES.map((r) => (
              <button key={r.key} data-testid={`pick-role-${r.key}`} onClick={() => { setRole(r.key); setStep(2); }} className="w-full text-left rounded-lg border border-slate-200 p-3 hover:border-[#1976D2] hover:bg-blue-50/40">
                <div className="text-sm font-semibold">{r.label.slice(0, -1)}</div>
                <div className="text-xs text-slate-500">Uses "{r.subLabel}" instead of specialty</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <Field label="Full name" required><input data-testid="s-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></Field>
            <Field label="Email" required><input data-testid="s-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></Field>
            <Field label={editMode ? "New password (leave blank to keep)" : "Password"} required={!editMode}><input data-testid="s-pwd" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder={editMode ? "•••••••" : ""} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></Field>
            <Field label={isDoctor ? "Specialty" : "Department"}><input data-testid={`s-${subField}`} value={form[subField]} onChange={(e) => setForm({...form, [subField]: e.target.value})} placeholder={isDoctor ? "e.g. Pediatrics, Surgery" : "e.g. Ward, Reception, Reagents"} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></Field>
            <Field label="Phone"><input data-testid="s-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" /></Field>
          </div>
        )}
        {step === 2 && (
          <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
            {!editMode && <button onClick={() => setStep(1)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium">Back</button>}
            <button onClick={onClose} className="h-9 px-3 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="s-save" disabled={!valid || busy} onClick={submit} className="h-9 px-4 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">{busy ? "Saving…" : (editMode ? "Save changes" : "Add staff")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}{required && <span className="text-[#B71C1C]"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
